/**
 * stores/session.js — logged-in user, account, channels.
 *
 * #108: the JWT lives ONLY in memory (the $jwt atom). It is never
 * written to localStorage. The HttpOnly `inbox_jwt` cookie set by
 * verifyOtp is the persistent credential — on a fresh page load the
 * boot flow (app.js init) calls /refresh with the cookie as fallback
 * auth, receives a new JWT in the response body, and stores it in
 * $jwt for the lifetime of the page.
 *
 * Why this matters: localStorage is readable from any JavaScript
 * running on the same origin, so an XSS on engage.odivend.com could
 * exfiltrate a 7-day session token. The HttpOnly cookie is not
 * readable from JS, so the same XSS can't steal the credential.
 *
 * Session *data* (accountId, email, channels, permissions) is still
 * cached in localStorage for fast boot UX — it's metadata, not a
 * credential. Stealing the metadata reveals who the user is but
 * doesn't grant impersonation.
 */

import { atom, computed } from 'nanostores';

const SESSION_DATA_KEY = 'inbox_v2_session_data';

// #108 one-shot migration: wipe the pre-#108 JWT key from localStorage
// so an XSS running on the same origin can't replay the old token until
// it expires. Idempotent no-op for any user who logged in after the
// deploy (the key was never written for them).
try { localStorage.removeItem('inbox_v2_session_jwt'); } catch {}

// ── Atoms ────────────────────────────────────────────────────────────────────

// $jwt is in-memory only (see file header). Boot flow populates it via
// /refresh with cookie fallback.
export const $jwt     = atom(null);
export const $session = atom(_loadCachedSession());

// Derived: is the user authenticated?
export const $isAuthed = computed([$jwt, $session], (jwt, session) => !!(jwt && session));

// ── Helpers ──────────────────────────────────────────────────────────────────

function _loadCachedSession() {
  try {
    const raw = localStorage.getItem(SESSION_DATA_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function setSession(jwt, sessionData) {
  $jwt.set(jwt);
  $session.set(sessionData);
  // JWT stays in memory only. Session data is fast-boot metadata cache.
  try {
    localStorage.setItem(SESSION_DATA_KEY, JSON.stringify(sessionData));
  } catch { /* quota */ }
}

// Inbox 9.3 (2026-06-04) — patch the channels list reactively after the
// manual Refresh Channels action returns. Mirrors the localStorage cache
// so a hard reload picks up the fresh list on next boot too.
export function updateSessionChannels(channels) {
  const current = $session.get();
  if (!current) return;
  const next = {
    ...current,
    channels,
    wabaId:         (channels && channels[0]?.waba_id) || current.wabaId,
    phoneNumberId:  (channels && channels[0]?.phone_number_id) || current.phoneNumberId,
  };
  $session.set(next);
  try { localStorage.setItem(SESSION_DATA_KEY, JSON.stringify(next)); } catch { /* quota */ }
}

export function clearSession() {
  $jwt.set(null);
  $session.set(null);
  try {
    localStorage.removeItem(SESSION_DATA_KEY);
    // Clear the legacy JWT key from pre-#108 installs — idempotent no-op
    // for anyone who's already past the migration.
    localStorage.removeItem('inbox_v2_session_jwt');
  } catch {}
}

// ── Convenience getters (for non-reactive reads) ─────────────────────────────

export const getJwt        = () => $jwt.get();
export const getSession    = () => $session.get();
export const getAccountId  = () => $session.get()?.accountId || null;
export const getAgentEmail = () => $session.get()?.agentEmail || null;
export const isOwner       = () => $session.get()?.userType === 'owner';
